<template>
  <div ref="dropdownRef" class="dropdown">
    <DropdownHeaderMapMenu :title="title" :isOpen="isOpen" @toggle="toggleDropdown" />
    <div class="dropdown-content" :class="{ open: isOpen }">
      <DropdownCheckboxRowMapMenu
        v-for="option in options"
        :id="option.id"
        :key="option.id"
        :checked="option.checked"
        :value="option.value"
        :circleColor="option.color"
        :iconSource="option.iconSource"
        :isHeading="option.isHeading"
        @toggleOption="onToggleOption"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { toRefs, ref, onMounted, onUnmounted } from 'vue'
import DropdownHeaderMapMenu from '@/components/general/selectors/DropdownMapMenu/DropdownHeaderMapMenu.vue'
import DropdownCheckboxRowMapMenu from '@/components/general/selectors/DropdownMapMenu/checkbox/DropdownCheckboxRowMapMenu.vue'
export type optionType = {
  id: string
  checked: boolean
  value: string
  color?: string
  iconSource?: string
  isHeading?: boolean
}
interface Props {
  title: string
  options: optionType[]
  preventCloseElementIds?: string[]
}

const props = withDefaults(defineProps<Props>(), { preventCloseElementIds: () => [] })
const emits = defineEmits<{ (e: 'toggleOption', id: string): void }>()
const { title, options, preventCloseElementIds } = toRefs(props)

const isOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)
onMounted(() =>
  document.addEventListener('click', (event: MouseEvent | PointerEvent) => close(event))
)
onUnmounted(() =>
  document.removeEventListener('click', (event: MouseEvent | PointerEvent) => close(event))
)
const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}
const onToggleOption = (id: string) => {
  emits('toggleOption', id)
}
const close = (event: MouseEvent | TouchEvent) => {
  const element = event.target as HTMLElement
  if (preventCloseElementIds.value.includes(element.id)) return
  const target = event.target as HTMLElement
  if (!dropdownRef.value?.contains(target)) isOpen.value = false
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/mixins.scss';
@use '@/styles/variables.scss';
.dropdown {
  width: 100%;
  min-width: 100px;
  z-index: 10;
  position: relative;
}
.dropdown-content {
  background-color: variables.$color-grey-blue-dark;
  margin-top: 10px;
  border-radius: 0px 0px variables.$border-radius-medium variables.$border-radius-medium;
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.25);
  opacity: 0;
  pointer-events: none;
  transform: translateY(-10px);
  transition: opacity 150ms ease-in-out, transform 150ms ease-in-out;
  font-weight: 600;
  font-size: 12px;
  overflow-y: auto;
  max-height: 0;
  @include mixins.scrollbar-vertical;
}
.dropdown-content.open {
  opacity: 1;
  pointer-events: auto;
  max-height: 25vh;
}
</style>
